import { StrictMode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { HelmetProvider } from 'react-helmet-async';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ClerkProvider } from '@clerk/react';
import { enUS, esES, ptBR } from '@clerk/localizations';
import { SnackbarProvider } from 'notistack';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/es';

import './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { CapabilityProvider } from './contexts/CapabilityContext';
import { VotacaoProvider } from './contexts/VotacaoContext';

// Estilos do MUI no começo do <head>: o CSS do Clerk (e qualquer outro) vem depois e
// ganha nos empates de especificidade.
const cacheEmotion = createCache({ key: 'css', prepend: true });

const CLERK_POR_IDIOMA = { pt: ptBR, en: enUS, es: esES };
const DAYJS_POR_IDIOMA = { pt: 'pt-br', en: 'en', es: 'es' };

/**
 * Providers globais do app, de fora pra dentro: estilo, <head>, datas, Clerk,
 * notificações e os contextos do Konq (auth → tenant → capabilities → votação).
 */
export default function AppProviders({ children }) {
  const { i18n } = useTranslation();
  const idioma = (i18n.language || 'pt').slice(0, 2).toLowerCase();
  // Fixo na montagem: trocar a localização re-monta o ClerkProvider por dentro.
  const localizacaoClerk = useMemo(() => CLERK_POR_IDIOMA[idioma] ?? ptBR, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StrictMode>
      <CacheProvider value={cacheEmotion}>
        <HelmetProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={DAYJS_POR_IDIOMA[idioma] ?? 'pt-br'}>
            <ClerkProvider
              publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
              localization={localizacaoClerk}
              // konq-ui e backoffice usam a MESMA instância do Clerk. Sem fixar as URLs aqui,
              // os passos de auth sem redirect explícito caíam no Account Portal da instância
              // (cujo Home é o backoffice) e o produtor/jurado ia parar lá no meio do cadastro.
              signInUrl="/login"
              signUpUrl="/registre-se"
              signInFallbackRedirectUrl="/dashboard"
              signUpFallbackRedirectUrl="/dashboard"
              afterSignOutUrl="/login"
            >
              <SnackbarProvider
                maxSnack={3}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={3000}
              >
                <AuthProvider>
                  <TenantProvider>
                    <CapabilityProvider>
                      <VotacaoProvider>{children}</VotacaoProvider>
                    </CapabilityProvider>
                  </TenantProvider>
                </AuthProvider>
              </SnackbarProvider>
            </ClerkProvider>
          </LocalizationProvider>
        </HelmetProvider>
      </CacheProvider>
    </StrictMode>
  );
}
